import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import AdminStatCard from '../../components/admin/AdminStatCard';
import {
  formatAdminAnswerTime,
  formatAdminAverageAnswerTime,
  formatAdminDateTime,
  formatAdminDuration,
} from '../../components/admin/adminFormatting';
import MeetingStatusBadge from '../../components/admin/MeetingStatusBadge';
import ActionsMenu from '../../components/common/ActionsMenu';
import { Button, Card, Input, PageHeader } from '@copilot/ui';
import { EmptyState, Modal } from '@copilot/ui';
import Icon from '../../components/common/Icon';
import { Field } from '../../components/common/Field';
import Loader from '../../components/common/Loader';
import * as adminApi from '../../services/adminApi';

// Failed and cancelled AI requests are tracked apart from the answer times
// shown on this page and never enter them; this says how many there were.
function answerTimingNote(timing) {
  const parts = [];
  if (timing?.failed_answers) parts.push(`${timing.failed_answers} failed`);
  if (timing?.cancelled_answers) parts.push(`${timing.cancelled_answers} cancelled`);
  return parts.length ? parts.join(' · ') : undefined;
}

function AdminMeetingDetailsPage() {
  const { meetingId } = useParams();

  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // Meeting conversation content is sensitive - never fetched until the
  // admin explicitly asks for it (see adminApi.getAdminMeetingContent's
  // caller here, only invoked from loadContent below).
  const [content, setContent] = useState(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const result = await adminApi.getAdminMeeting(meetingId);
        if (!cancelled) setDetail(result);
      } catch (err) {
        if (!cancelled) {
          if (err.status === 404) {
            setError('Meeting not found.');
          } else if (err.status === 403) {
            setError('You do not have permission to view meetings.');
          } else {
            setError(err.message || 'Unable to load meeting.');
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [meetingId, reloadToken]);

  const loadContent = async () => {
    setIsContentLoading(true);
    setContentError('');

    try {
      const result = await adminApi.getAdminMeetingContent(meetingId);
      setContent(result);
    } catch (err) {
      setContentError(err.message || 'Unable to load conversation.');
    } finally {
      setIsContentLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteReason('');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError('');

    try {
      await adminApi.deleteAdminMeeting(meetingId, deleteReason.trim());
      closeDeleteModal();
      setDeleteMessage('Meeting moved to trash.');
      setTimeout(() => setDeleteMessage(''), 2500);
      setReloadToken((t) => t + 1);
    } catch (err) {
      setDeleteError(err.message || 'Unable to move meeting to trash.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async () => {
    setIsRestoring(true);
    setDeleteError('');

    try {
      await adminApi.restoreAdminMeeting(meetingId);
      setDeleteMessage('Meeting restored.');
      setTimeout(() => setDeleteMessage(''), 2500);
      setReloadToken((t) => t + 1);
    } catch (err) {
      setDeleteError(err.message || 'Unable to restore meeting.');
    } finally {
      setIsRestoring(false);
    }
  };

  if (isLoading) {
    return <Loader label="Loading meeting..." />;
  }

  if (error) {
    return (
      <div>
        <Link to="/admin/meetings" className="admin-back-link">
          <Icon name="logout" className="icon-flip" /> Back to Meetings
        </Link>
        <EmptyState title="Unable to load meeting" description={error} />
      </div>
    );
  }

  const { meeting, stats } = detail;
  const isDeleted = Boolean(meeting.deleted_at);
  const timing = meeting.ai_answer_timing;

  return (
    <div>
      <Link to="/admin/meetings" className="admin-back-link">
        <Icon name="logout" className="icon-flip" /> Back to Meetings
      </Link>

      <PageHeader
        title={meeting.title || `Meeting #${meeting.id}`}
        description={`Owned by ${meeting.owner.full_name} (${meeting.owner.email})`}
      />

      <div className="stat-cards admin-stat-cards">
        <AdminStatCard title="Transcripts" value={stats.transcript_count} icon="activity" />
        <AdminStatCard title="Questions" value={stats.question_count} icon="question" />
        <AdminStatCard title="AI Responses" value={stats.ai_response_count} icon="activity" />
      </div>

      <div className="stat-cards admin-stat-cards">
        <AdminStatCard
          title="Average Time"
          value={formatAdminAverageAnswerTime(timing?.average_ms, stats.ai_response_count)}
          icon="clock"
        />
        <AdminStatCard
          title="Fastest Time"
          value={formatAdminAnswerTime(timing?.fastest_ms)}
          icon="clock"
        />
        <AdminStatCard
          title="Slowest Time"
          value={formatAdminAnswerTime(timing?.slowest_ms)}
          icon="clock"
        />
        <AdminStatCard
          title="Total AI Answers"
          value={timing?.successful_answers || '—'}
          icon="sparkles"
          description={answerTimingNote(timing)}
        />
      </div>

      <div className="admin-detail-grid">
        <div>
          <Card interactive title="Meeting Information">
            <div className="admin-info-list">
              <div className="admin-info-row">
                <span className="admin-info-row-label">Meeting ID</span>
                <span className="admin-info-row-value">#{meeting.id}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Title</span>
                <span className="admin-info-row-value">{meeting.title || '—'}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Owner</span>
                <span className="admin-info-row-value">{meeting.owner.full_name}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Status</span>
                <span className="admin-info-row-value">
                  <MeetingStatusBadge status={meeting.status} deletedAt={meeting.deleted_at} />
                </span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Created At</span>
                <span className="admin-info-row-value">{formatAdminDateTime(meeting.created_at)}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Started At</span>
                <span className="admin-info-row-value">{formatAdminDateTime(meeting.started_at)}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Ended At</span>
                <span className="admin-info-row-value">{formatAdminDateTime(meeting.ended_at)}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Duration</span>
                <span className="admin-info-row-value">{formatAdminDuration(meeting.duration_seconds)}</span>
              </div>
            </div>
          </Card>

          <Card interactive title="Language &amp; Providers" className="admin-section-heading">
            <div className="admin-info-list">
              <div className="admin-info-row">
                <span className="admin-info-row-label">Source Language</span>
                <span className="admin-info-row-value">{meeting.source_language}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Target Language</span>
                <span className="admin-info-row-value">{meeting.target_language}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">STT Provider</span>
                <span className="admin-info-row-value">{meeting.stt_provider}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">TTS Provider</span>
                <span className="admin-info-row-value">{meeting.tts_provider}</span>
              </div>
              <div className="admin-info-row">
                <span className="admin-info-row-label">Translation Provider</span>
                <span className="admin-info-row-value">{meeting.translation_provider}</span>
              </div>
            </div>
          </Card>

          <Card interactive title="Conversation" className="admin-section-heading">
            {!content && !isContentLoading && (
              <>
                <p className="admin-action-status">
                  Meeting conversation data is sensitive and is not loaded automatically.
                </p>
                <Button onClick={loadContent}>View Conversation</Button>
                {contentError && <div className="form-alert">{contentError}</div>}
              </>
            )}

            {isContentLoading && <Loader label="Loading conversation..." />}

            {content && (
              <>
                <h4>Transcript</h4>
                {content.transcripts.length === 0 ? (
                  <EmptyState title="No transcript entries." />
                ) : (
                  <div className="admin-content-list">
                    {content.transcripts.map((entry) => (
                      <div className="admin-content-row" key={entry.id}>
                        <span className="admin-content-speaker">{entry.speaker || 'Unknown'}</span>
                        <span className="admin-content-primary">{entry.original_text}</span>
                        <span className="admin-content-meta">{formatAdminDateTime(entry.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <h4 className="admin-section-heading">AI Responses</h4>
                {content.ai_responses.length === 0 ? (
                  <EmptyState title="No AI responses." />
                ) : (
                  <div className="admin-content-list">
                    {content.ai_responses.map((entry) => (
                      <div className="admin-content-row" key={entry.id}>
                        <span className="admin-content-primary">{entry.question}</span>
                        <span className="admin-content-primary">{entry.answer}</span>
                        <span className="admin-content-meta">{formatAdminDateTime(entry.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        <Card interactive title="Manage Meeting">
          <div className="admin-action-list">
            {isDeleted && (
              <p className="admin-action-status">
                This meeting is in the trash. Its transcripts, AI responses
                and usage history are preserved.
                {meeting.deleted_reason ? ` Reason: ${meeting.deleted_reason}` : ''}
              </p>
            )}

            <div className="admin-action-row">
              <span className="admin-info-row-label">Actions</span>
              <ActionsMenu
                items={[
                  isDeleted
                    ? {
                        label: isRestoring ? 'Restoring...' : 'Restore Meeting',
                        onClick: handleRestore,
                        disabled: isRestoring,
                      }
                    : {
                        label: 'Move to Trash',
                        onClick: () => setShowDeleteModal(true),
                        variant: 'danger',
                      },
                ]}
              />
            </div>

            {deleteError && !showDeleteModal && (
              <p className="form-alert">{deleteError}</p>
            )}
            {deleteMessage && <p className="admin-action-status success">{deleteMessage}</p>}
          </div>
        </Card>
      </div>

      {showDeleteModal && (
        <Modal
          title="Move meeting to trash?"
          onClose={() => !isDeleting && closeDeleteModal()}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? 'Working...' : 'Move to Trash'}
              </Button>
            </>
          }
        >
          <p>Are you sure you want to move this item to trash?</p>
          <p className="field-help">
            The meeting disappears from its owner&apos;s history and from the
            default admin list. Nothing is erased: transcripts, AI responses
            and the minutes already billed are preserved, and you can restore
            it at any time.
          </p>

          <Field label="Reason (optional)" htmlFor="meeting-delete-reason">
            <Input
              id="meeting-delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Why is this meeting being removed?"
              maxLength={500}
            />
          </Field>

          {deleteError && (
            <p className="form-alert" style={{ marginTop: 'var(--space-3)' }}>
              {deleteError}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

export default AdminMeetingDetailsPage;
